import React, { useId } from 'react';
import { useCanvasStore } from '@/stores/canvasStore';

interface OnboardingBackgroundProps {
  children: React.ReactNode;
}

/**
 * Full-viewport background with a dark canvas + slowly pulsating dot grid.
 * Mimics the app's infinite canvas feel during onboarding.
 * Adapts subtly to dark/light mode.
 */
export function OnboardingBackground({ children }: OnboardingBackgroundProps) {
  const patternId = useId();
  const gridSize = 24;
  const dotRadius = 1.2;
  const isDarkMode = useCanvasStore((s) => s.isDarkMode);

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden"
      style={{ background: isDarkMode ? '#0c0c10' : '#111114' }}
    >
      {/* Animated dot grid */}
      <div className="absolute inset-0 pointer-events-none onboarding-grid-pulse">
        <svg width="100%" height="100%" style={{ display: 'block' }}>
          <defs>
            <pattern
              id={patternId}
              width={gridSize}
              height={gridSize}
              patternUnits="userSpaceOnUse"
            >
              <circle
                cx={gridSize / 2}
                cy={gridSize / 2}
                r={dotRadius}
                fill="rgba(255, 255, 255, 0.12)"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill={`url(#${patternId})`} />
        </svg>
      </div>

      {/* Subtle radial glow behind the card */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 60% 50% at 50% 50%, rgba(99, 102, 241, 0.08) 0%, transparent 70%)',
        }}
      />

      {/* Content */}
      <div className="relative z-10 w-full flex items-center justify-center px-4">
        {children}
      </div>

      <style>{`
        @keyframes gridPulse {
          0%, 100% { opacity: 0.35; }
          50% { opacity: 0.7; }
        }
        .onboarding-grid-pulse {
          animation: gridPulse 6s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
